import React, { useState } from "react";
import { KeyRound, ShieldQuestion, ArrowLeft, Link2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "./ui/input-otp";
import { api, errMsg } from "../lib/api";

export const PASSWORD_RULES_HINT = "Min 8 characters, with at least 1 uppercase letter and 1 number.";

const CHANNEL_LABELS = { email: "email", sms: "SMS", whatsapp: "WhatsApp" };

/**
 * Two-step Forgot Password dialog used on every login screen.
 * Step 1: identify account (email/mobile) + delivery channel (email / SMS / WhatsApp)
 * Step 2: the message carries a one-time RESET LINK (Phase 125) that opens /reset-password; as a fallback the
 *         6-digit code from the same message can be typed here together with a new password.
 *
 * variant "tenant" -> /t/{muniCode}/auth/*   (staff + citizens)
 * variant "platform" -> /platform/auth/*     (super admins)
 */
export const ForgotPasswordDialog = ({ open, onOpenChange, variant = "tenant", muniCode = "", defaultEmail = "" }) => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState(defaultEmail);
  const [channel, setChannel] = useState("email");
  const [otp, setOtp] = useState("");
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const base = variant === "platform" ? "/platform/auth" : `/t/${muniCode}/auth`;

  const reset = () => { setStep(1); setOtp(""); setPw1(""); setPw2(""); setBusy(false); };
  const handleOpenChange = (v) => { if (!v) reset(); onOpenChange(v); };

  React.useEffect(() => { if (open && defaultEmail) setEmail(defaultEmail); }, [open, defaultEmail]);

  const sendOtp = async (e) => {
    e.preventDefault();
    if (variant === "tenant" && !muniCode) return toast.error("Select your municipality first");
    setBusy(true);
    try {
      // origin lets the server build the reset link for this deployment when no public base URL is configured
      const r = await api.post(`${base}/forgot-password`, { email: email.trim(), channel, origin: window.location.origin });
      toast.success(r.data.message || "Reset link sent");
      setStep(2);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) return toast.error("Enter the 6-digit code from the message");
    if (pw1 !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      const r = await api.post(`${base}/reset-password`, { email: email.trim(), otp, new_password: pw1 });
      toast.success(r.data.message || "Password reset successfully");
      handleOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-sm" data-testid="forgot-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ShieldQuestion className="h-4 w-4" /> Reset your password
          </DialogTitle>
          <DialogDescription>
            {step === 1
              ? "We'll send you a one-time password reset link (valid for 10 minutes)."
              : `Check your ${CHANNEL_LABELS[channel]} and open the reset link to choose a new password.`}
          </DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <form onSubmit={sendOtp} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="fp-email">Email or mobile</Label>
              <Input id="fp-email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com or 10-digit mobile" required data-testid="forgot-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Send the reset link via</Label>
              <RadioGroup value={channel} onValueChange={setChannel} className="flex gap-4 flex-wrap">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="email" id="fp-ch-email" data-testid="forgot-channel-email" />
                  <Label htmlFor="fp-ch-email" className="font-normal cursor-pointer">Email</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="sms" id="fp-ch-sms" data-testid="forgot-channel-sms" />
                  <Label htmlFor="fp-ch-sms" className="font-normal cursor-pointer">SMS</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="whatsapp" id="fp-ch-whatsapp" data-testid="forgot-channel-whatsapp" />
                  <Label htmlFor="fp-ch-whatsapp" className="font-normal cursor-pointer">WhatsApp</Label>
                </div>
              </RadioGroup>
            </div>
            <Button type="submit" className="w-full" disabled={busy} data-testid="forgot-send-otp-button">
              <Link2 className="h-4 w-4 mr-2" /> {busy ? "Sending..." : "Send reset link"}
            </Button>
          </form>
        ) : (
          <form onSubmit={doReset} className="space-y-4">
            <div className="rounded-lg border bg-muted/40 px-3 py-2 text-xs text-muted-foreground" data-testid="forgot-link-sent-note">
              The link works once and expires in 10 minutes. Can't open it? The same message carries a 6-digit code — enter it below.
            </div>
            <div className="space-y-1.5">
              <Label>6-digit code</Label>
              <div className="flex justify-center" data-testid="forgot-otp-input">
                <InputOTP maxLength={6} value={otp} onChange={setOtp}>
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}
                  </InputOTPGroup>
                </InputOTP>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="fp-pw1">New password</Label>
              <Input id="fp-pw1" type="password" value={pw1} onChange={(e) => setPw1(e.target.value)}
                required data-testid="forgot-new-password-input" />
              <p className="text-[11px] text-muted-foreground">{PASSWORD_RULES_HINT}</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="fp-pw2">Confirm new password</Label>
              <Input id="fp-pw2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)}
                required data-testid="forgot-confirm-password-input" />
            </div>
            <Button type="submit" className="w-full" disabled={busy} data-testid="forgot-reset-button">
              <KeyRound className="h-4 w-4 mr-2" /> {busy ? "Resetting..." : "Reset password"}
            </Button>
            <button type="button" onClick={() => { setStep(1); setOtp(""); }}
              className="w-full text-xs text-muted-foreground hover:text-foreground flex items-center justify-center gap-1"
              data-testid="forgot-back-button">
              <ArrowLeft className="h-3 w-3" /> Didn't get it? Send again
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

/** Small "Forgot password?" trigger link for login forms. */
export const ForgotPasswordLink = ({ onClick, testId = "forgot-password-link" }) => (
  <button type="button" onClick={onClick}
    className="text-xs text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
    data-testid={testId}>
    Forgot password?
  </button>
);
